import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useToast } from '../../components/ui/Toast';
import { date, fine, money } from '../../utils/format';
import {
  Drawer,
  Field,
  GstinInput,
  NumberInput,
  PanInput,
  PhoneInput,
  Select,
  TextInput,
} from '../../components/ui/Form';
import { ErrorNotice, Loading } from '../../components/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';

interface Detail {
  id: string;
  code: string;
  name: string;
  type: string;
  is_active: boolean;
  phone: string | null;
  email: string | null;
  gstin: string | null;
  pan: string | null;
  address: Record<string, string> | null;
  place_of_supply: string | null;
  credit_limit: string;
  credit_days: number;
  advance_held?: string;
  is_blocked: boolean;
  block_reason: string | null;
  outstanding: string;
  invoice_count: string;
  lifetime_value: string;
  last_bought_on: string | null;
  recentSales: { id: string; doc_no: string; doc_date: string; grand_total: string; balance_amount: string }[];
  oldGold: { id: string; doc_no: string; doc_date: string; grand_total: string; fine_wt: string }[];
}

const line = (label: string, value: string | null | undefined): JSX.Element | null =>
  value ? (
    <div className="flex gap-3 py-1 text-sm">
      <span className="w-28 shrink-0 text-ink-soft">{label}</span>
      <span className="num">{value}</span>
    </div>
  ) : null;

/**
 * One customer, as a shop thinks of them.
 *
 * Not a list of columns: what they owe comes first, because that is the
 * question being asked when somebody walks in, and the old gold they have
 * brought in sits beside what they have bought — in this trade the two are the
 * same relationship seen from either end.
 */
export function CustomerDrawer({ customerId, onClose }: { customerId: string; onClose: () => void }) {
  const { can } = usePermission();
  const client = useQueryClient();
  const toast = useToast();
  const onEnter = useKeyboardForm();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [confirmInactive, setConfirmInactive] = useState(false);

  const {
    data,
    error: loadError,
    isLoading,
  } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => api.get<Detail>(`/customers/${customerId}`),
  });

  const beginEdit = (): void => {
    if (!data) return;
    const a = data.address ?? {};
    setForm({
      name: data.name,
      type: data.type,
      phone: data.phone ?? '',
      email: data.email ?? '',
      gstin: data.gstin ?? '',
      pan: data.pan ?? '',
      line1: a['line1'] ?? '',
      line2: a['line2'] ?? '',
      city: a['city'] ?? '',
      state: a['state'] ?? '',
      pincode: a['pincode'] ?? '',
      placeOfSupply: data.place_of_supply ?? '',
      creditLimit: data.credit_limit ?? '',
      creditDays: String(data.credit_days ?? ''),
    });
    setEditing(true);
  };

  const save = useMutation({
    mutationFn: () =>
      api.edit(`/customers/${customerId}`, {
        name: form.name,
        type: form.type,
        phone: form.phone || null,
        email: form.email || null,
        gstin: form.gstin || null,
        pan: form.pan || null,
        address: {
          ...(form.line1 ? { line1: form.line1 } : {}),
          ...(form.line2 ? { line2: form.line2 } : {}),
          ...(form.city ? { city: form.city } : {}),
          ...(form.state ? { state: form.state } : {}),
          ...(form.pincode ? { pincode: form.pincode } : {}),
        },
        placeOfSupply: form.placeOfSupply || null,
        ...(form.creditLimit ? { creditLimit: form.creditLimit } : {}),
        ...(form.creditDays ? { creditDays: Number(form.creditDays) } : {}),
      }),
    onSuccess: () => {
      toast('Customer updated');
      setEditing(false);
      setError(null);
      void client.invalidateQueries({ queryKey: ['customer', customerId] });
      void client.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: setError,
  });

  const toggleActive = useMutation({
    mutationFn: (isActive: boolean) => api.edit(`/customers/${customerId}`, { isActive }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['customer', customerId] });
      void client.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    save.mutate();
  };
  const set = (k: string, v: string): void => setForm((f) => ({ ...f, [k]: v }));

  const address = data?.address ?? {};
  const postal = [address['line1'], address['line2'], address['city'], address['state'], address['pincode']]
    .filter(Boolean)
    .join(', ');

  const STATES_HINT = 'Tamil Nadu';

  return (
    <Drawer title={data?.name ?? 'Customer'} subtitle={data?.code} onClose={onClose}>
      <ErrorNotice error={error ?? loadError} />
      {isLoading ? <Loading rows={3} /> : null}

      {data && editing ? (
        <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-3">
          <Field label="Name" required>
            <TextInput value={form.name ?? ''} onChange={(v) => set('name', v)} maxLength={200} autoFocus />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <Select
                value={form.type ?? 'RETAIL'}
                onChange={(v) => set('type', v)}
                options={[
                  { value: 'RETAIL', label: 'Retail' },
                  { value: 'WHOLESALE', label: 'Wholesale' },
                  { value: 'WALKIN', label: 'Walk-in' },
                ]}
              />
            </Field>
            <Field label="Mobile">
              <PhoneInput value={form.phone ?? ''} onChange={(v) => set('phone', v)} />
            </Field>
          </div>
          <Field label="Email">
            <TextInput value={form.email ?? ''} onChange={(v) => set('email', v)} maxLength={200} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="GSTIN">
              <GstinInput value={form.gstin ?? ''} onChange={(v) => set('gstin', v)} />
            </Field>
            <Field label="PAN">
              <PanInput value={form.pan ?? ''} onChange={(v) => set('pan', v)} />
            </Field>
          </div>
          <Field label="Street">
            <TextInput value={form.line1 ?? ''} onChange={(v) => set('line1', v)} maxLength={200} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="City">
              <TextInput value={form.city ?? ''} onChange={(v) => set('city', v)} maxLength={100} />
            </Field>
            <Field label="State">
              <TextInput
                value={form.state ?? ''}
                onChange={(v) => {
                  set('state', v);
                  if (!form.placeOfSupply) set('placeOfSupply', v);
                }}
                maxLength={100}
                placeholder={STATES_HINT}
              />
            </Field>
            <Field label="Pincode">
              <TextInput value={form.pincode ?? ''} onChange={(v) => set('pincode', v)} maxLength={6} allow={/[0-9]/} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Credit limit">
              <NumberInput
                value={form.creditLimit ?? ''}
                onChange={(v) => set('creditLimit', v)}
                decimals={2}
                suffix="₹"
              />
            </Field>
            <Field label="Days to pay">
              <NumberInput
                value={form.creditDays ?? ''}
                onChange={(v) => set('creditDays', v)}
                decimals={0}
                max={365}
              />
            </Field>
          </div>
          <div className="flex gap-2 pt-1">
            <button className="btn-primary" disabled={save.isPending || !(form.name ?? '').trim()}>
              {save.isPending ? 'Saving…' : 'Save changes'}
            </button>
            <button type="button" className="btn-ghost" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {data && !editing && (can('customer.edit') || data.is_active === false) ? (
        <div className="mb-4 flex items-center gap-2">
          {can('customer.edit') ? (
            <button className="btn-ghost py-1 text-xs" onClick={beginEdit}>
              Edit details
            </button>
          ) : null}
          {/* Making a customer inactive is this app's delete — the owner's call. */}
          {can('document.void') ? (
            data.is_active === false ? (
              <button className="btn-ghost py-1 text-xs" onClick={() => toggleActive.mutate(true)}>
                Reactivate
              </button>
            ) : (
              <button
                className="py-1 text-xs text-danger underline"
                onClick={() => {
                  setConfirmInactive(true);
                }}
              >
                Make inactive
              </button>
            )
          ) : null}
          {data.is_active === false ? <span className="chip-mute">inactive</span> : null}
        </div>
      ) : null}

      {data && !editing ? (
        <div className="space-y-5">
          {data.is_blocked ? (
            <p className="rounded border-l-2 border-danger bg-danger/5 px-3 py-2 text-sm text-danger">
              This account is blocked. {data.block_reason}
            </p>
          ) : null}

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-md border border-silver-100 bg-surface px-3 py-2">
              <p className="text-2xs text-ink-soft">{Number(data.outstanding) < 0 ? 'In credit' : 'Owes now'}</p>
              <p
                className={`num text-lg font-semibold ${
                  Number(data.outstanding) > 0 ? 'text-danger' : Number(data.outstanding) < 0 ? 'text-success' : ''
                }`}
              >
                {money(Math.abs(Number(data.outstanding)).toFixed(2))}
              </p>
            </div>
            <div className="rounded-md border border-silver-100 bg-surface px-3 py-2">
              <p className="text-2xs text-ink-soft">Bought in total</p>
              <p className="num text-lg font-semibold">{money(data.lifetime_value)}</p>
            </div>
            <div className="rounded-md border border-silver-100 bg-surface px-3 py-2">
              <p className="text-2xs text-ink-soft">Invoices</p>
              <p className="num text-lg font-semibold">{data.invoice_count}</p>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border border-silver-100 bg-surface px-3 py-2">
            <div>
              <p className="text-2xs text-ink-soft">Advance held with the shop</p>
              <p className={`num text-lg font-semibold ${Number(data.advance_held ?? 0) > 0 ? 'text-success' : ''}`}>
                {money(data.advance_held ?? '0')}
              </p>
            </div>
            <div className="text-right text-xs">
              <Link className="underline" to="/advances?side=CUSTOMER">
                {can('payment.create') ? 'Take or refund an advance' : 'See advances'}
              </Link>
              {Number(data.advance_held ?? 0) > 0 ? (
                <p className="mt-0.5 text-2xs text-ink-soft">The till uses it first on their next bill.</p>
              ) : null}
            </div>
          </div>

          {Number(data.credit_limit) > 0 ? (
            <p className="text-2xs text-ink-faint">
              Credit limit {money(data.credit_limit)}
              {data.credit_days > 0 ? `, ${data.credit_days} days` : ''}
              {data.last_bought_on ? ` · last bought ${date(data.last_bought_on)}` : ''}
            </p>
          ) : null}

          <section>
            <h3 className="mb-1 text-sm font-semibold">Details</h3>
            <div className="rounded-md border border-silver-100 bg-surface px-3 py-2">
              {line('Phone', data.phone ? `+91 ${data.phone}` : null)}
              {line('Email', data.email)}
              {line('GSTIN', data.gstin)}
              {line('PAN', data.pan)}
              {line('Place of supply', data.place_of_supply)}
              {postal ? (
                <div className="flex gap-3 py-1 text-sm">
                  <span className="w-28 shrink-0 text-ink-soft">Address</span>
                  <span>{postal}</span>
                </div>
              ) : null}
              {!data.phone && !data.email && !data.gstin && !postal ? (
                <p className="py-1 text-sm text-ink-soft">
                  Nothing recorded beyond a name. An invoice over ₹50,000 needs an address.
                </p>
              ) : null}
            </div>
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold">Recent invoices</h3>
            {data.recentSales.length === 0 ? (
              <p className="rounded-md border border-silver-100 bg-surface px-3 py-3 text-sm text-ink-soft">
                Nothing bought yet.
              </p>
            ) : (
              <ul className="overflow-hidden rounded-md border border-silver-100 bg-surface">
                {data.recentSales.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-baseline gap-3 border-b border-silver-100/70 px-3 py-2 text-sm last:border-0"
                  >
                    {can('sales.view') ? (
                      <Link className="num underline" to={`/sales/${s.id}/print`}>
                        {s.doc_no}
                      </Link>
                    ) : (
                      <span className="num">{s.doc_no}</span>
                    )}
                    <span className="text-ink-soft">{date(s.doc_date)}</span>
                    <span className="num ml-auto">{money(s.grand_total)}</span>
                    {Number(s.balance_amount) > 0 ? (
                      <span className="num text-2xs text-danger">{money(s.balance_amount)} due</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {data.oldGold.length > 0 ? (
            <section>
              <h3 className="mb-1 text-sm font-semibold">Old gold brought in</h3>
              <ul className="overflow-hidden rounded-md border border-silver-100 bg-surface">
                {data.oldGold.map((g) => (
                  <li
                    key={g.id}
                    className="flex items-baseline gap-3 border-b border-silver-100/70 px-3 py-2 text-sm last:border-0"
                  >
                    <span className="num">{g.doc_no}</span>
                    <span className="text-ink-soft">{date(g.doc_date)}</span>
                    <span className="num ml-auto">{fine(g.fine_wt)}</span>
                    <span className="num text-2xs text-ink-soft">{money(g.grand_total)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
      <ConfirmDialog
        open={confirmInactive}
        tone="danger"
        title="Make this customer inactive?"
        message="They stay on record with all their history — bills, old gold, savings — but cannot be put on a new bill."
        confirmLabel="Make inactive"
        busy={toggleActive.isPending}
        onCancel={() => setConfirmInactive(false)}
        onConfirm={() => {
          toggleActive.mutate(false);
          setConfirmInactive(false);
        }}
      />
    </Drawer>
  );
}
